import OpenInNew from "@mui/icons-material/OpenInNew";
import {
  Box,
  Link as MuiLink,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { getStatusLabel } from "encamp-shared/src/submissions";
import {
  GeneratorStatusExceedanceDetailQuery,
  GeneratorStatusExceedanceResolutionType,
  ResolutionStatus,
} from "generated-graphql/graphql";
import { Link } from "react-router-dom";

import { RelatedFindings } from "./CalculatedStatusHistory";
import { DrawerDisclosure } from "./DrawerDisclosure";
import { drawerSectionSx, DrawerSectionHeading } from "./DrawerSection";
import { ExceedanceForDrawer } from "./ExceedanceActionDrawer";
import { ExceedanceMetaLine } from "./ExceedanceMetaLine";
import { FindingHistory } from "./FindingHistory";
import { ManifestCorrectionsTable } from "./ManifestCorrectionsTable";
import { formatPeriodMonth } from "./periodFormat";
import { RcraInfoCatchUpNote } from "./RcraInfoCatchUpNote";
import { ReviewStatusSummary } from "./ReviewStatusSummary";

type Detail = GeneratorStatusExceedanceDetailQuery["generatorStatusExceedanceDetail"];

export function ReviewFollowUp({
  detail,
  exceedance,
  validatedAt,
  resolutionMethod,
  loading,
  error,
  siblingDrawerSearch,
}: {
  detail?: Detail;
  exceedance: ExceedanceForDrawer;
  validatedAt?: string | null;
  resolutionMethod: string | null;
  loading: boolean;
  error: boolean;
  siblingDrawerSearch: (period: string) => string;
}) {
  const linkedOrOpenSubmission = exceedance.linkedSubmission ?? exceedance.openSubmission;
  return (
    <Box sx={drawerSectionSx}>
      <DrawerSectionHeading component="h3">Review status</DrawerSectionHeading>
      <Stack spacing={2}>
        {loading && !detail ? (
          <Skeleton height={60} />
        ) : (
          <ReviewStatusSummary
            status={detail?.resolution?.status ?? exceedance.resolutionStatus}
            validatedAt={validatedAt}
            events={error ? undefined : detail?.events}
            hasLinkedSubmission={Boolean(exceedance.linkedSubmission)}
            hasCorrections={Boolean(detail?.manifestCorrections.length)}
          />
        )}
        <ExceedanceMetaLine
          validatedAt={validatedAt}
          resolutionMethod={resolutionMethod}
          staffAssigneeName={exceedance.staffAssigneeName}
        />
        <RcraInfoCatchUpNote
          catchUp={detail?.rcraInfoCatchUp}
          showState={Boolean(exceedance.stateDirection)}
        />
        <CorrectionConfirmationNote
          resolution={detail?.resolution}
          mismatchMonths={exceedance.mismatchMonths}
        />

        {/* Linked submission — an explicitly linked submission (any type) or
                the auto-detected open notification driving the status. */}
        {linkedOrOpenSubmission && (
          <Box>
            <Typography variant="subtitle2" component="h4">
              Linked submission
            </Typography>
            <MuiLink
              component={Link}
              to={`/o/${exceedance.tenantId}/waste/submissions/${linkedOrOpenSubmission.id}/edit`}
              target="_blank"
              rel="noopener noreferrer"
              sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}
            >
              <OpenInNew fontSize="small" />
              {linkedOrOpenSubmission.title} ({getStatusLabel(linkedOrOpenSubmission.status)})
            </MuiLink>
          </Box>
        )}

        {/* Manifest corrections cited as the resolution — only ever present on
                a MANIFEST_CORRECTION resolution. */}
        <ManifestCorrectionsTable
          corrections={detail?.manifestCorrections ?? []}
          tenantId={exceedance.tenantId}
        />
        <ManifestCorrectionsTable
          corrections={detail?.previousManifestCorrections ?? []}
          tenantId={exceedance.tenantId}
          title="Previously linked corrections"
        />

        {!error && <FindingHistory events={detail?.events} loading={loading} />}
        {detail && (
          <RelatedFindings
            findings={detail.relatedDiscrepancies}
            facilityState={exceedance.state}
            siblingDrawerSearch={siblingDrawerSearch}
          />
        )}
        {detail && <AccumulationHistory months={detail.wadmByMonth} />}
      </Stack>
    </Box>
  );
}

/** The sweep doesn't reopen these while the mismatch persists, so the drawer says it's unconfirmed. */
export function CorrectionConfirmationNote({
  resolution,
  mismatchMonths,
}: {
  resolution?: Pick<NonNullable<Detail["resolution"]>, "status" | "resolutionType"> | null;
  mismatchMonths?: number | null;
}) {
  const unconfirmed =
    resolution?.status === ResolutionStatus.Resolved &&
    resolution.resolutionType === GeneratorStatusExceedanceResolutionType.ManifestCorrection &&
    (mismatchMonths ?? 0) > 0;
  if (!unconfirmed) return null;
  return (
    <Box>
      <Typography variant="subtitle2" component="h4">
        Not confirmed yet
      </Typography>
      <Typography variant="body2">
        The corrections are marked fixed, but the calculated status still differs from the reported
        status. It should clear once the corrected manifest data is imported.
      </Typography>
    </Box>
  );
}

function AccumulationHistory({ months }: { months: Detail["wadmByMonth"] }) {
  if (!months.length) return null;
  return (
    <Box>
      <DrawerDisclosure title="WADM — hazardous waste accumulation (month by month)">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Month</TableCell>
              <TableCell align="right">Weight (lbs)</TableCell>
              <TableCell align="right">Items</TableCell>
              <TableCell>WADM status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {months.map((m) => (
              <TableRow key={m.periodStart}>
                <TableCell>{formatPeriodMonth(m.periodStart)}</TableCell>
                <TableCell align="right">
                  {m.totalWeightLbs.toLocaleString("en-US", {
                    maximumFractionDigits: 1,
                  })}
                </TableCell>
                <TableCell align="right">{m.itemCount}</TableCell>
                <TableCell>{m.wadmGeneratorStatus ?? "—"}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </DrawerDisclosure>
    </Box>
  );
}
